import * as turf from '@turf/turf';

const GRID_SIZE = 0.5;

let grid = new Map();
let stations = new Map();
let initialized = false;

function getGridKey(lng, lat) {
  const gridX = Math.floor(lng / GRID_SIZE);
  const gridY = Math.floor(lat / GRID_SIZE);
  return `${gridX},${gridY}`;
}

function getGridCellsForBounds(bounds) {
  const cells = [];
  const startX = Math.floor(bounds.west / GRID_SIZE);
  const endX = Math.floor(bounds.east / GRID_SIZE);
  const startY = Math.floor(bounds.south / GRID_SIZE);
  const endY = Math.floor(bounds.north / GRID_SIZE);

  for (let x = startX; x <= endX; x++) {
    for (let y = startY; y <= endY; y++) {
      cells.push(`${x},${y}`);
    }
  }
  return cells;
}

function stationInBounds(station, bounds) {
  return station.longitude >= bounds.west && 
         station.longitude <= bounds.east &&
         station.latitude >= bounds.south && 
         station.latitude <= bounds.north;
}

export function updateIndex(newStations) {
  if (!Array.isArray(newStations) || newStations.length === 0) return;

  for (const station of newStations) {
    if (!station?.id) continue;

    const existingStation = stations.get(station.id);
    
    if (existingStation) {
      if (existingStation.longitude !== station.longitude || 
          existingStation.latitude !== station.latitude) {
        const oldKey = getGridKey(existingStation.longitude, existingStation.latitude);
        const oldCell = grid.get(oldKey);
        if (oldCell) {
          oldCell.delete(station.id);
          if (oldCell.size === 0) {
            grid.delete(oldKey);
          }
        }
      } else {
        continue;
      }
    }

    const gridKey = getGridKey(station.longitude, station.latitude);
    if (!grid.has(gridKey)) {
      grid.set(gridKey, new Set());
    }
    grid.get(gridKey).add(station.id);
    stations.set(station.id, station);
  }

  initialized = true;
}

export function queryBounds(bounds) {
  if (!initialized) return [];

  const cells = getGridCellsForBounds(bounds);
  const results = [];
  const seen = new Set();

  for (const cellKey of cells) {
    const cell = grid.get(cellKey);
    if (!cell) continue;

    for (const stationId of cell) {
      if (seen.has(stationId)) continue;
      seen.add(stationId);

      const station = stations.get(stationId);
      if (station && stationInBounds(station, bounds)) {
        results.push(station);
      }
    }
  }

  return results;
}

export function findStationsInFeature(feature) {
  if (!initialized) {
    console.warn('SpatialIndex not initialized');
    return [];
  }
  
  let bounds;
  
  if (feature.properties?.isCircle && feature.properties.center) {
    const { center, radiusKm } = feature.properties;
    const radius = radiusKm || 0;
    const radiusDeg = radius / 111;
    
    bounds = {
      west: center[0] - radiusDeg,
      east: center[0] + radiusDeg,
      south: center[1] - radiusDeg,
      north: center[1] + radiusDeg
    };
  } else {
    try {
      const bbox = turf.bbox(feature);
      bounds = {
        west: bbox[0],
        south: bbox[1],
        east: bbox[2],
        north: bbox[3]
      };
    } catch (e) {
      console.error('Failed to calculate bbox:', e);
      return [];
    }
  }
  
  const candidates = queryBounds(bounds);
  
  const filtered = candidates.filter(station => {
    const point = [station.longitude, station.latitude];
    
    if (feature.properties?.isCircle && feature.properties.center) {
      const { center, radiusKm } = feature.properties;
      const radius = radiusKm;
      try {
        const distance = turf.distance(
          turf.point(center), 
          turf.point(point), 
          { units: 'kilometers' }
        );
        return distance <= radius;
      } catch (e) {
        console.error('Distance calculation failed:', e);
        return false;
      }
    }
    
    try {
      if (feature.geometry && feature.geometry.type === 'Polygon') {
        const polygon = turf.polygon(feature.geometry.coordinates);
        return turf.booleanPointInPolygon(turf.point(point), polygon);
      }
      return false;
    } catch (e) {
      console.error('Point-in-polygon check failed:', e);
      return false;
    }
  });
  
  return filtered;
}

export function clear() {
  grid.clear();
  stations.clear();
  initialized = false;
}

export function isInitialized() {
  return initialized;
}